import { Component } from "@theme/component";
import { ThemeEvents, CartUpdateEvent, DiscountUpdateEvent } from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from "@theme/utilities";

const CART_ITEMS_COMPONENT = "cart-items-component";

// Cart page and drawer are two views of one server resource. Every observed
// synchronization intent—local /cart/change, foreign cart update/add, or
// discount update—claims one shared authority id. A WeakMap makes every
// component listener reuse the same id for a given Event instance.
let latestCartSyncIntentId = 0;
/** @type {WeakMap<Event, number>} */
const cartSyncIntentIdsByEvent = new WeakMap();
const CART_SYNC_INTENT_PARAM = "_cart_sync_intent";
const CART_LINE_INPUT_SELECTOR = "input[data-cart-line]";
const CART_DRAWER_DIALOG_SELECTOR = ".cart-drawer__dialog";
const CART_DRAWER_EMPTY_CLASS = "cart-drawer--empty";
const CART_DRAWER_LABELLED_BY_ATTRIBUTE = "aria-labelledby";
const CART_SUMMARY_STICKY_ATTRIBUTE = "cart-summary-sticky";

/** @returns {number} The new page-wide cart synchronization intent id. */
const beginCartSyncIntent = () => {
  latestCartSyncIntentId += 1;
  return latestCartSyncIntentId;
};

/** @param {CartMutationContext} context @returns {boolean} */
const isCurrentCartMutation = context => context.id === latestCartSyncIntentId;

/** @param {number} intentId @returns {boolean} */
const isCurrentCartSyncIntent = intentId => intentId === latestCartSyncIntentId;

/**
 * Claims one authority id per observed Event, shared by page/drawer listeners.
 * Locally dispatched events are pre-tagged with their mutation context id.
 * @param {Event} event
 * @returns {number}
 */
const getCartSyncIntentId = event => {
  const existingIntentId = cartSyncIntentIdsByEvent.get(event);
  if (existingIntentId !== undefined) {
    return existingIntentId;
  }

  const intentId = beginCartSyncIntent();
  cartSyncIntentIdsByEvent.set(event, intentId);
  return intentId;
};

/**
 * Gives SectionRenderer a request-authority-specific pending/cache key while
 * preserving the storefront URL and Shopify's section_id handling.
 * @param {number} intentId
 * @returns {URL}
 */
const getCartSyncSectionUrl = intentId => {
  const url = new URL(globalThis.location.href);
  url.searchParams.set(CART_SYNC_INTENT_PARAM, String(intentId));
  return url;
};

// Matches ERROR_MESSAGE_DISPLAY_DURATION in product-form.js
const ERROR_MESSAGE_DISPLAY_DURATION = 10_000;
/** @type {Record<string, string>} */
const STATUS_TOKENS = {
  item: "__item__",
  quantity: "__quantity__",
  subtotal: "__subtotal__",
  error: "__error__",
};

/**
 * Identity key for cart line-item rows during morph reconciliation.
 *
 * The cart `<tr>` rows carry `data-key="{{ item.key }}"` (unique and stable per
 * line) but no `id`. Without a key fn, morph's getNodeKey falls back to
 * `node.id`, finds none, and matches rows by POSITION. That can repaint the
 * NEXT surviving item's content into a removed row whenever a reduced-motion,
 * foreign-surface, or interrupted local morph reconciles the table. Keying by
 * `data-key` makes every path match rows by identity: survivors keep their own
 * nodes and the doomed row is removed by key. The normal local path additionally
 * waits for its collapse motion before committing the morph.
 *
 * Returns null for non-row elements so the rest of the cart subtree keeps
 * id-based keying (unchanged behavior). Mirrors the scoped getNodeKey in
 * variant-picker.js. MUST stay in lockstep with the `data-key` attribute in
 * snippets/cart-products.liquid.
 *
 * @param {Node | undefined} node - The node being keyed by the morph.
 * @returns {string | undefined} The line-item key, or undefined to fall back to id keying.
 */
const cartItemRowKey = node => (node instanceof HTMLElement ? (node.dataset.key ?? undefined) : undefined);

/**
 * Finds the surviving row whose terminal spacing must settle while the
 * currently rendered last row is removed.
 * @param {HTMLTableRowElement[]} rows - Cart rows in rendered order.
 * @param {Set<HTMLTableRowElement>} removedRows - Rows included in the mutation.
 * @returns {HTMLTableRowElement[]} Zero or one row that will become last.
 */
const getSettlingRows = (rows, removedRows) => {
  const lastRenderedRow = rows.at(-1);
  if (!lastRenderedRow || !removedRows.has(lastRenderedRow)) {
    return [];
  }

  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const candidate = rows[index];
    if (candidate && !removedRows.has(candidate)) {
      return [candidate];
    }
  }

  return [];
};

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @typedef {Object} DrawerDialogState
 * @property {boolean} isEmpty - Whether the drawer was styled as empty.
 * @property {string | null} labelledBy - The dialog's accessible-name reference.
 * @property {string | null} summarySticky - The drawer summary overflow mode.
 */

/**
 * @typedef {Object} CartMutationContext
 * @property {number} id - Monotonic mutation id used to suppress stale announcements.
 * @property {number} line - One-based cart line index.
 * @property {number} requestedQuantity - Quantity requested by the customer.
 * @property {string} action - Mutation action used for performance instrumentation.
 * @property {string} lineKey - Stable Shopify cart line key.
 * @property {string} itemName - Product and selected variant name.
 * @property {HTMLElement | null} initiator - Control that initiated the mutation.
 * @property {HTMLInputElement | null} quantityInput - Original quantity input, retained for rollback.
 * @property {string} controlSelector - Selector for the equivalent control on another row.
 * @property {string | null} nextLineKey - Next surviving row key for removal focus.
 * @property {string | null} previousLineKey - Previous surviving row key for removal focus.
 * @property {HTMLTableRowElement[]} removingRows - Parent and nested rows affected by a removal.
 * @property {HTMLTableRowElement[]} settlingRows - Surviving rows whose last-row spacing must collapse with a removal.
 * @property {Promise<void>} removalAnimationFinished - Resolves when all optimistic row motion has settled.
 * @property {Node[] | null} rollbackNodes - Original drawer children retained during optimistic empty state.
 * @property {DrawerDialogState | null} rollbackDrawerState - Original outer drawer state retained for rollback.
 * @property {boolean} emptyCartTransitionPending - Whether this request owns the empty-page transition.
 */

/**
 * @typedef {Object} CartMutationConfig
 * @property {number} line - One-based cart line index.
 * @property {number} quantity - Requested quantity.
 * @property {string} action - Mutation action used for performance instrumentation.
 * @property {CartMutationContext} [context] - Pre-captured removal context.
 */

/**
 * @typedef {Object} CartChangeResponse
 * @property {Record<string, string>} sections - Requested section HTML keyed by section id.
 * @property {Array<{key: string, quantity: number, variant_id: number}>} [items] - Server-confirmed cart lines.
 */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [quantitySelectors] - The quantity selector elements.
 * @property {HTMLTableRowElement[]} [cartItemRows] - The cart item rows.
 * @property {TextComponent} [cartTotal] - The cart total.
 * @property {HTMLElement} [cartStatus] - Dedicated atomic mutation status.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #errorTimeouts = new Map();

  /**
   * Handles the discount update.
   * @param {Event} event - The event.
   */
  handleDiscountUpdate = event => {
    const customEvent = /** @type {DiscountUpdateEvent} */ (event);
    this.#handleCartUpdate(customEvent);
  };

  /**
   * Handles the cart error.
   * @param {CartMutationContext} context - Captured mutation identity and focus state.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = async (context, parsedResponseText) => {
    const message = String(parsedResponseText.errors);

    if (!isCurrentCartMutation(context)) {
      return;
    }

    // Re-render the section to sync UI with the actual cart state. Shopify may
    // have capped the quantity to the max available, so the server-rendered
    // quantities, line prices, and cart total can differ from the user's input.
    // The error message is shown AFTER the morph to avoid a flash (morph resets
    // the error container to hidden).
    try {
      // Fetch without rendering: renderSection() can commit its morph before
      // its promise settles, which would let a recovery that became stale
      // during the await overwrite a newer cart result.
      const [sections, cartResponse] = await Promise.all([
        this.#fetchAuthoritativeSections(context.id),
        fetch("/cart.js"),
      ]);

      if (!isCurrentCartMutation(context)) {
        return;
      }

      const cart = await cartResponse.json();

      if (!isCurrentCartMutation(context)) {
        return;
      }

      const sectionHtml = sections[this.sectionId];
      if (typeof sectionHtml !== "string") {
        throw new TypeError("Cart recovery section HTML missing");
      }

      this.#updateQuantitySelectors(cart);

      // Dispatch CartUpdateEvent so other components (cart summary, icon) update
      const parsed = new DOMParser().parseFromString(sectionHtml, "text/html");
      const itemCountText = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
      const itemCount = itemCountText ? Number.parseInt(itemCountText, 10) : 0;

      const cartUpdateEvent = new CartUpdateEvent(cart, this.sectionId, {
        itemCount,
        sections,
        source: CART_ITEMS_COMPONENT,
      });
      cartSyncIntentIdsByEvent.set(cartUpdateEvent, context.id);
      this.dispatchEvent(cartUpdateEvent);

      if (!isCurrentCartMutation(context)) {
        return;
      }

      morphSection(this.sectionId, sectionHtml, {
        mode: this.isDrawer ? "hydration" : "full",
        getNodeKey: cartItemRowKey,
      });

      if (!isCurrentCartMutation(context)) {
        return;
      }

      // The server-rendered cart is authoritative; discard the optimistic
      // drawer rollback snapshot rather than re-inserting stale state.
      this.#commitOptimisticMutation(context);
      this.#updateCartQuantitySelectorButtonStates();
      this.#showCartItemError(context.line, message);
    } catch {
      if (!isCurrentCartMutation(context)) {
        return;
      }

      // If recovery cannot reach the server, restore the exact optimistic
      // drawer nodes and the original field value so the initiating control
      // remains available.
      this.#restoreOptimisticMutation(context);
      const quantityInput = this.#findLineControl(context.lineKey, CART_LINE_INPUT_SELECTOR);
      const inputToRestore = quantityInput instanceof HTMLInputElement ? quantityInput : context.quantityInput;

      if (inputToRestore instanceof HTMLInputElement) {
        inputToRestore.value = inputToRestore.defaultValue;
      }

      this.#showCartItemError(context.line, message);
    }

    if (!isCurrentCartMutation(context)) {
      return;
    }

    this.#announceError(context, message);
    this.#restoreFailureFocus(context);
  };

  /**
   * Handles the cart update.
   *
   * @param {Event} event
   */
  #handleCartUpdate = event => {
    const customEvent = /** @type {DiscountUpdateEvent | CartUpdateEvent | import("@theme/events").CartAddEvent} */ (
      event
    );
    if (customEvent instanceof DiscountUpdateEvent) {
      const discountIntentId = getCartSyncIntentId(customEvent);
      if (!isCurrentCartSyncIntent(discountIntentId)) {
        return;
      }

      this.#fetchAndMorphCartSection(discountIntentId);
      return;
    }
    if (customEvent.target === this) {
      return;
    }

    const intentId = getCartSyncIntentId(customEvent);
    if (!isCurrentCartSyncIntent(intentId)) {
      return;
    }

    const cartItemsHtml = customEvent.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, { getNodeKey: cartItemRowKey });

      if (!isCurrentCartSyncIntent(intentId)) {
        return;
      }

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#releaseCartSyncIntent(intentId);
    } else {
      this.#fetchAndMorphCartSection(intentId);
    }
  };

  /**
   * Fetches a foreign-event section without committing until its shared intent
   * still owns authority. The intent-specific URL prevents SectionRenderer from
   * deduplicating this request onto an older pending promise.
   * @param {number} intentId - Shared synchronization authority id.
   */
  async #fetchAndMorphCartSection(intentId) {
    if (!isCurrentCartSyncIntent(intentId)) {
      return;
    }

    try {
      const sectionHtml = await sectionRenderer.getSectionHTML(this.sectionId, false, getCartSyncSectionUrl(intentId));

      if (!isCurrentCartSyncIntent(intentId)) {
        return;
      }

      morphSection(this.sectionId, sectionHtml, { getNodeKey: cartItemRowKey });

      if (!isCurrentCartSyncIntent(intentId)) {
        return;
      }

      this.#updateCartQuantitySelectorButtonStates();
      this.#releaseCartSyncIntent(intentId);
    } catch (error) {
      if (!isCurrentCartSyncIntent(intentId)) {
        return;
      }

      console.error(error);
      this.#releaseCartSyncIntent(intentId);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const timeout of this.#errorTimeouts.values()) {
      clearTimeout(timeout);
    }
    this.#errorTimeouts.clear();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Shows a cart item error message and auto-dismisses it after a timeout.
   * @param {number} line - The cart line number.
   * @param {string} message - The error message to display.
   */
  #showCartItemError(line, message) {
    const cartItemError = this.querySelector(`[ref="cartItemError-${line}"]`);
    const cartItemErrorContainer = this.querySelector(`[ref="cartItemErrorContainer-${line}"]`);

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      return;
    }

    cartItemError.textContent = message;
    cartItemErrorContainer.classList.remove("hidden");

    const existingTimeout = this.#errorTimeouts.get(line);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    this.#errorTimeouts.set(
      line,
      setTimeout(() => {
        cartItemErrorContainer.classList.add("hidden");
        this.#errorTimeouts.delete(line);
      }, ERROR_MESSAGE_DISPLAY_DURATION),
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    const customEvent = /** @type {import("@theme/events").QuantitySelectorUpdateEvent} */ (event);
    if (!(customEvent.target instanceof Node) || !this.contains(customEvent.target)) {
      return;
    }

    const { quantity, cartLine: line } = customEvent.detail;

    // Cart items require a line number
    if (!line) {
      return;
    }

    if (quantity === 0) {
      this.onLineItemRemove(line, customEvent);
      return;
    }

    const context = this.#createMutationContext({ line, quantity, action: "change" }, customEvent);
    this.updateQuantity({
      line,
      quantity,
      context,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows?.[line - 1];

    if (!lineItemRow) {
      return;
    }

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector("text-component"));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The initiating remove or quantity event.
   */
  onLineItemRemove(line, event) {
    const context = this.#createMutationContext({ line, quantity: 0, action: "clear" }, event);
    const mutationConfig = {
      line,
      context,
      quantity: 0,
      action: "clear",
    };

    if (context.removingRows.length === 0) {
      this.updateQuantity(mutationConfig);
      return;
    }

    // If the cart item row is the last row, optimistically trigger the cart empty state.
    const isEmptyCart = context.removingRows.length === (this.refs.cartItemRows?.length ?? 0);

    // The empty-cart transition differs by surface.
    //
    // DRAWER: inject the optimistic data-empty-cart-template immediately inside a view
    // transition. The drawer is a self-contained dialog with no sibling sections,
    // so the optimistic swap perturbs nothing and gives instant feedback.
    //
    // PAGE: defer the transition to the server morph instead of injecting
    // optimistically. Replacing the main cart subtree before /cart/change
    // returns exposes hand-built empty-state geometry while the rest of the
    // document (including the footer) is still laid out around the populated
    // cart. Waiting lets the typed transition move between two authoritative,
    // fully laid-out states; CSS gives the footer its own stable participant.
    // The cart dims and the total shimmers while the request is in flight.
    if (isEmptyCart && !this.isDrawer) {
      context.emptyCartTransitionPending = true;
      this.updateQuantity(mutationConfig);
      return;
    }

    const template = this.#getEmptyCartTemplate();
    if (isEmptyCart && this.isDrawer && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      const status = this.#getCartStatus();

      // Retain the exact live nodes for a network rollback. Re-inserting these
      // nodes (rather than cloned markup) preserves the initiating control and
      // its focus identity if the clear fails.
      context.rollbackNodes = [...this.childNodes];
      context.rollbackDrawerState = this.#captureDrawerState();

      startViewTransition(
        () => {
          if (!isCurrentCartMutation(context)) {
            return;
          }

          this.replaceChildren(clone);

          if (status) {
            this.append(status);
          }

          this.#setDrawerEmptyState();
          this.updateQuantity(mutationConfig);
        },
        ["empty-cart-drawer"],
        { transitionRoot: this },
      );

      return;
    }

    if (!prefersReducedMotion()) {
      // Start every affected animation in the same style update. If the final
      // rendered row is being removed, its preceding survivor also sheds the
      // spacing/border it will lose when it becomes :last-of-type; otherwise
      // that structural style change lands as a second, late layout jump.
      // Measure every border box before writing animation styles. clientHeight
      // excludes the divider, which makes a bordered row lose one pixel as soon
      // as its border-box `height` animation begins.
      const removingRowMeasurements = context.removingRows.map(row => ({
        row,
        height: row.getBoundingClientRect().height,
      }));

      for (const { row, height } of removingRowMeasurements) {
        row.style.setProperty("--row-height", `${height}px`);
        row.classList.add("removing");
      }

      for (const row of context.settlingRows) {
        row.classList.add("cart-items__table-row--becoming-last");
      }

      context.removalAnimationFinished = onAnimationEnd([...context.removingRows, ...context.settlingRows], undefined, {
        subtree: false,
      });
    }

    this.updateQuantity(mutationConfig);
  }

  /**
   * Updates the quantity.
   * @param {CartMutationConfig} config - The mutation configuration.
   * @returns {Promise<void>}
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const context = config.context ?? this.#createMutationContext(config);

    // A removal context can be captured before an asynchronous View Transition
    // update callback runs. If another page/drawer mutation has started in that
    // gap, reject the delayed callback before it disables UI or sends a request.
    if (!isCurrentCartMutation(context)) {
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      return;
    }

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const body = this.#buildCartChangeBody(line, quantity);

    cartTotal?.shimmer();

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }));

      if (!isCurrentCartMutation(context)) {
        return;
      }

      const responseText = await response.text();

      if (!isCurrentCartMutation(context)) {
        return;
      }

      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponseText.errors) {
        await this.#handleCartError(context, parsedResponseText);
        return;
      }

      await this.#applySuccessfulMutation(context, parsedResponseText);
    } catch (error) {
      if (!isCurrentCartMutation(context)) {
        return;
      }

      resetShimmer(this);
      console.error(error);

      this.#restoreOptimisticMutation(context);
      const message = this.#formatStatus("statusNetworkError", { item: context.itemName });
      this.#showCartItemError(line, message);
      this.#announceMessage(context, message);
      this.#restoreFailureFocus(context);
    } finally {
      // Only the request that still owns the shared cart version can release
      // pending UI. An older page or drawer request must not re-enable either
      // surface while the newer transaction is still in flight.
      if (isCurrentCartMutation(context)) {
        this.#releaseCartSyncIntent(context.id);
      }
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /** @returns {string[]} Every rendered cart section id. */
  #getCartSectionIds() {
    const sectionIds = new Set([this.sectionId]);

    for (const item of document.querySelectorAll(CART_ITEMS_COMPONENT)) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    }

    return [...sectionIds];
  }

  /**
   * Fetches recovery markup without committing DOM side effects.
   * @param {number} intentId - Mutation authority id used to isolate pending requests.
   * @returns {Promise<Record<string, string>>}
   */
  async #fetchAuthoritativeSections(intentId) {
    const entries = await Promise.all(
      this.#getCartSectionIds().map(async sectionId => [
        sectionId,
        await sectionRenderer.getSectionHTML(sectionId, false, getCartSyncSectionUrl(intentId)),
      ]),
    );

    return Object.fromEntries(entries);
  }

  /**
   * Builds one cart/change body that keeps all rendered cart surfaces in sync.
   * @param {number} line - One-based cart line index.
   * @param {number} quantity - Requested line quantity.
   * @returns {string}
   */
  #buildCartChangeBody(line, quantity) {
    return JSON.stringify({
      line,
      quantity,
      sections: this.#getCartSectionIds().join(","),
      sections_url: globalThis.location.pathname,
    });
  }

  /**
   * Applies the authoritative response and emits one local status/focus result.
   * @param {CartMutationContext} context - Captured mutation context.
   * @param {CartChangeResponse} parsedResponse - Parsed Shopify cart response.
   */
  async #applySuccessfulMutation(context, parsedResponse) {
    if (!isCurrentCartMutation(context)) {
      return;
    }

    const sectionHtml = parsedResponse.sections?.[this.sectionId];

    if (typeof sectionHtml !== "string") {
      throw new TypeError("Cart section HTML missing");
    }

    const newSection = new DOMParser().parseFromString(sectionHtml, "text/html");
    const itemCountText = newSection.querySelector('[ref="cartItemCount"]')?.textContent;
    const itemCount = itemCountText ? Number.parseInt(itemCountText, 10) : 0;

    this.#updateQuantitySelectors(parsedResponse);
    const cartUpdateEvent = new CartUpdateEvent(parsedResponse, this.sectionId, {
      itemCount,
      source: CART_ITEMS_COMPONENT,
      sections: parsedResponse.sections,
    });
    cartSyncIntentIdsByEvent.set(cartUpdateEvent, context.id);
    this.dispatchEvent(cartUpdateEvent);

    if (!isCurrentCartMutation(context)) {
      return;
    }

    await this.#morphSuccessfulSection(context, sectionHtml);

    if (!isCurrentCartMutation(context)) {
      return;
    }

    this.#commitOptimisticMutation(context);
    this.#updateCartQuantitySelectorButtonStates();

    if (context.requestedQuantity === 0) {
      this.#announceRemoval(context);
      this.#recoverRemovalFocus(context);
      return;
    }

    const updatedLine = Array.isArray(parsedResponse.items)
      ? parsedResponse.items.find(item => item.key === context.lineKey)
      : undefined;
    const resultingQuantity = Number(updatedLine?.quantity ?? context.requestedQuantity);
    this.#announceQuantity(context, resultingQuantity);
  }

  /**
   * Morphs the returned cart section, preserving the existing empty-page transition.
   * @param {CartMutationContext} context - Captured mutation context.
   * @param {string} sectionHtml - Authoritative section HTML.
   */
  async #morphSuccessfulSection(context, sectionHtml) {
    if (!isCurrentCartMutation(context)) {
      return;
    }

    // A fast /cart/change response must not interrupt the local collapse at an
    // arbitrary frame. Commit the keyed server morph only once the removal and
    // any newly-last survivor have reached the exact server-rendered geometry.
    await context.removalAnimationFinished;

    if (!isCurrentCartMutation(context)) {
      return;
    }

    if (context.emptyCartTransitionPending) {
      /** @type {Promise<void>} */
      const morphCommitted = new Promise((resolve, reject) => {
        startViewTransition(() => {
          if (!isCurrentCartMutation(context)) {
            resolve();
            return;
          }

          try {
            morphSection(this.sectionId, sectionHtml, {
              mode: "full",
              getNodeKey: cartItemRowKey,
            });
            resolve();
          } catch (error) {
            reject(error);
          }
        }, ["empty-cart-page"]).catch(reject);
      });
      await morphCommitted;
      return;
    }

    if (!isCurrentCartMutation(context)) {
      return;
    }

    morphSection(this.sectionId, sectionHtml, {
      mode: this.isDrawer ? "hydration" : "full",
      getNodeKey: cartItemRowKey,
    });
  }

  /** @param {CartMutationContext} context - Mutation context. */
  #commitOptimisticMutation(context) {
    this.#syncDrawerStateAfterMorph(context);
    context.rollbackNodes = null;
    context.rollbackDrawerState = null;
  }

  /**
   * Captures mutation identity before any optimistic DOM change or server morph.
   * @param {CartMutationConfig} config - The mutation configuration.
   * @param {Event} [event] - The event that initiated the mutation.
   * @returns {CartMutationContext}
   */
  #createMutationContext(config, event) {
    const rows = this.refs.cartItemRows ?? [];
    const row = rows[config.line - 1];
    const removingRows = [];

    if (config.quantity === 0 && row) {
      removingRows.push(row);

      for (const candidate of rows) {
        if (candidate.dataset.parentKey === row.dataset.key) {
          removingRows.push(candidate);
        }
      }
    }

    const removedRows = new Set(removingRows);
    const settlingRows = getSettlingRows(rows, removedRows);
    const rowIndex = row ? rows.indexOf(row) : -1;
    let nextLineKey = null;
    let previousLineKey = null;

    for (let index = rowIndex + 1; index < rows.length; index += 1) {
      const candidate = rows[index];

      if (candidate && !removedRows.has(candidate)) {
        nextLineKey = candidate.dataset.key ?? null;
        break;
      }
    }

    for (let index = rowIndex - 1; index >= 0; index -= 1) {
      const candidate = rows[index];

      if (candidate && !removedRows.has(candidate)) {
        previousLineKey = candidate.dataset.key ?? null;
        break;
      }
    }

    const initiator = this.#getMutationInitiator(row, event);
    const itemName =
      row?.dataset.itemName?.trim() || row?.querySelector(".cart-items__title")?.textContent?.trim() || "";
    const id = beginCartSyncIntent();

    return {
      id,
      itemName,
      initiator,
      nextLineKey,
      previousLineKey,
      removingRows,
      settlingRows,
      removalAnimationFinished: Promise.resolve(),
      line: config.line,
      requestedQuantity: config.quantity,
      action: config.action,
      lineKey: row?.dataset.key ?? "",
      quantityInput: row?.querySelector(CART_LINE_INPUT_SELECTOR) ?? null,
      controlSelector: this.#controlSelectorFor(initiator),
      rollbackNodes: null,
      rollbackDrawerState: null,
      emptyCartTransitionPending: false,
    };
  }

  /**
   * Resolves the concrete initiating control while its row is still live.
   * @param {HTMLTableRowElement | undefined} row - Current cart row.
   * @param {Event} [event] - Mutation event.
   * @returns {HTMLElement | null}
   */
  #getMutationInitiator(row, event) {
    const activeElement = document.activeElement;

    if (
      activeElement instanceof HTMLElement &&
      row?.contains(activeElement) &&
      this.#isMutationControl(activeElement)
    ) {
      return activeElement;
    }

    if (event?.target instanceof HTMLElement) {
      const eventControl = event.target.closest(
        'button[name="minus"], button[name="plus"], input[data-cart-line], .cart-items__remove',
      );

      if (eventControl instanceof HTMLElement && row?.contains(eventControl)) {
        return eventControl;
      }

      const eventInput = event.target.querySelector(CART_LINE_INPUT_SELECTOR);

      if (eventInput instanceof HTMLInputElement && row?.contains(eventInput)) {
        return eventInput;
      }
    }

    return row?.querySelector(CART_LINE_INPUT_SELECTOR) ?? null;
  }

  /**
   * @param {HTMLElement} element - Candidate cart mutation control.
   * @returns {boolean}
   */
  #isMutationControl(element) {
    return element.matches('button[name="minus"], button[name="plus"], input[data-cart-line], .cart-items__remove');
  }

  /** @returns {DrawerDialogState | null} Current outer drawer state. */
  #captureDrawerState() {
    const dialog = this.closest(CART_DRAWER_DIALOG_SELECTOR);
    if (!(dialog instanceof HTMLDialogElement)) {
      return null;
    }

    return {
      isEmpty: dialog.classList.contains(CART_DRAWER_EMPTY_CLASS),
      labelledBy: dialog.getAttribute(CART_DRAWER_LABELLED_BY_ATTRIBUTE),
      summarySticky: dialog.getAttribute(CART_SUMMARY_STICKY_ATTRIBUTE),
    };
  }

  /**
   * Matches the optimistic drawer to server-rendered initial-empty geometry.
   * The sticky=false attribute is load-bearing: it releases overflow on the
   * items container so the empty CTA's hover shadow is not clipped.
   */
  #setDrawerEmptyState() {
    const dialog = this.closest(CART_DRAWER_DIALOG_SELECTOR);
    if (!(dialog instanceof HTMLDialogElement)) {
      return;
    }

    dialog.classList.add(CART_DRAWER_EMPTY_CLASS);
    dialog.setAttribute(CART_DRAWER_LABELLED_BY_ATTRIBUTE, "cart-drawer-heading-empty");
    dialog.setAttribute(CART_SUMMARY_STICKY_ATTRIBUTE, "false");
  }

  /** @param {CartMutationContext} context - Mutation context. */
  #restoreDrawerState(context) {
    const state = context.rollbackDrawerState;
    context.rollbackDrawerState = null;

    if (!state) {
      return;
    }

    const dialog = this.closest(CART_DRAWER_DIALOG_SELECTOR);
    if (!(dialog instanceof HTMLDialogElement)) {
      return;
    }

    dialog.classList.toggle(CART_DRAWER_EMPTY_CLASS, state.isEmpty);

    if (state.labelledBy === null) {
      dialog.removeAttribute(CART_DRAWER_LABELLED_BY_ATTRIBUTE);
    } else {
      dialog.setAttribute(CART_DRAWER_LABELLED_BY_ATTRIBUTE, state.labelledBy);
    }

    if (state.summarySticky === null) {
      dialog.removeAttribute(CART_SUMMARY_STICKY_ATTRIBUTE);
    } else {
      dialog.setAttribute(CART_SUMMARY_STICKY_ATTRIBUTE, state.summarySticky);
    }
  }

  /**
   * Reconciles the optimistic outer drawer state with authoritative content.
   * @param {CartMutationContext} context - Mutation context.
   */
  #syncDrawerStateAfterMorph(context) {
    if (!context.rollbackDrawerState) {
      return;
    }

    if (this.querySelector(".cart-form")) {
      this.#restoreDrawerState(context);
      return;
    }

    this.#setDrawerEmptyState();
  }

  /**
   * Returns a stable selector for the same control type on another cart row.
   * @param {HTMLElement | null} element - Initiating control.
   * @returns {string}
   */
  #controlSelectorFor(element) {
    if (element?.matches('button[name="minus"]')) {
      return 'button[name="minus"]';
    }
    if (element?.matches('button[name="plus"]')) {
      return 'button[name="plus"]';
    }
    if (element?.matches(".cart-items__remove")) {
      return ".cart-items__remove";
    }

    return CART_LINE_INPUT_SELECTOR;
  }

  /**
   * Finds a stable row without interpolating an untrusted line key into CSS.
   * @param {string | null} lineKey - Shopify cart line key.
   * @returns {HTMLTableRowElement | null}
   */
  #findLineRow(lineKey) {
    if (!lineKey) {
      return null;
    }

    for (const row of this.querySelectorAll('[ref="cartItemRows[]"]')) {
      if (row instanceof HTMLTableRowElement && row.dataset.key === lineKey) {
        return row;
      }
    }

    return null;
  }

  /**
   * @param {string | null} lineKey - Shopify cart line key.
   * @param {string} selector - Equivalent control selector.
   * @returns {HTMLElement | null}
   */
  #findLineControl(lineKey, selector) {
    const control = this.#findLineRow(lineKey)?.querySelector(selector);
    return control instanceof HTMLElement && this.#canReceiveFocus(control) ? control : null;
  }

  /**
   * @param {HTMLElement} element - Candidate focus target.
   * @returns {boolean}
   */
  #canReceiveFocus(element) {
    if (element.hidden || element.closest("[hidden]")) {
      return false;
    }
    if ((element instanceof HTMLButtonElement || element instanceof HTMLInputElement) && element.disabled) {
      return false;
    }

    return true;
  }

  /**
   * Restores pending removal styling and exact drawer nodes after a failure.
   * @param {CartMutationContext} context - Mutation context.
   */
  #restoreOptimisticMutation(context) {
    if (context.rollbackNodes) {
      this.replaceChildren(...context.rollbackNodes);
      context.rollbackNodes = null;
    }

    this.#restoreDrawerState(context);

    for (const row of context.removingRows) {
      row.classList.remove("removing");
      row.style.removeProperty("--row-height");
    }

    for (const row of context.settlingRows) {
      row.classList.remove("cart-items__table-row--becoming-last");
    }
  }

  /**
   * Keeps focus on the initiating control after a failed mutation.
   * @param {CartMutationContext} context - Mutation context.
   */
  #restoreFailureFocus(context) {
    if (!isCurrentCartMutation(context)) {
      return;
    }

    if (context.initiator?.isConnected && this.#canReceiveFocus(context.initiator)) {
      context.initiator.focus();
      return;
    }

    this.#findLineControl(context.lineKey, context.controlSelector)?.focus();
  }

  /**
   * Moves focus after removal to the equivalent next-line control, previous
   * line, empty-cart heading, then drawer close control.
   * @param {CartMutationContext} context - Mutation context.
   */
  #recoverRemovalFocus(context) {
    if (!isCurrentCartMutation(context)) {
      return;
    }

    const adjacentControl =
      this.#findLineControl(context.nextLineKey, context.controlSelector) ??
      this.#findLineControl(context.previousLineKey, context.controlSelector);

    if (adjacentControl) {
      adjacentControl.focus();
      return;
    }

    const emptyHeading = this.querySelector(
      ".empty-state__heading, #cart-drawer-heading-empty, .cart-drawer__heading--empty",
    );

    if (emptyHeading instanceof HTMLElement) {
      emptyHeading.tabIndex = -1;
      emptyHeading.focus();
      return;
    }

    const closeControl =
      this.querySelector('[ref="closeButton"], .cart-drawer__close-button') ??
      this.closest("dialog")?.querySelector('[ref="closeButton"], .cart-drawer__close-button');

    if (closeControl instanceof HTMLElement && this.#canReceiveFocus(closeControl)) {
      closeControl.focus();
    }
  }

  /** @returns {HTMLElement | null} Dedicated atomic cart status. */
  #getCartStatus() {
    const status = this.querySelector('[ref="cartStatus"]');
    return status instanceof HTMLElement ? status : null;
  }

  /**
   * Finds only the empty template owned by this cart surface. The header drawer
   * and cart page can both be present in section-rendering responses, so a
   * document-wide ID lookup can clone the wrong surface.
   * @returns {HTMLTemplateElement | null}
   */
  #getEmptyCartTemplate() {
    const scope = this.closest("cart-drawer-component") ?? this.closest(".shopify-section") ?? this;
    const template = scope.querySelector("template[data-empty-cart-template]");
    return template instanceof HTMLTemplateElement ? template : null;
  }

  /** @returns {string} Current formatted cart subtotal when rendered. */
  #getCartSubtotal() {
    const subtotal = this.querySelector("[data-cart-subtotal]") ?? this.querySelector('[ref="cartTotal"]');

    if (!(subtotal instanceof HTMLElement)) {
      return "";
    }

    return (subtotal.getAttribute("value") || subtotal.textContent || "").trim();
  }

  /**
   * Formats a server-rendered localized status template.
   * @param {string} templateName - Dataset property containing the template.
   * @param {Record<string, string | number>} replacements - Token values.
   * @returns {string}
   */
  #formatStatus(templateName, replacements) {
    const template = this.#getCartStatus()?.dataset[templateName] ?? "";
    let message = template;

    for (const [name, value] of Object.entries(replacements)) {
      const token = STATUS_TOKENS[name];

      if (token) {
        message = message.replaceAll(token, String(value));
      }
    }

    return message.trim();
  }

  /**
   * Writes exactly one message for the latest local mutation.
   * @param {CartMutationContext} context - Mutation context.
   * @param {string} message - Localized announcement.
   */
  #announceMessage(context, message) {
    if (!message || !isCurrentCartMutation(context)) {
      return;
    }

    const status = this.#getCartStatus();
    if (!status) {
      return;
    }

    status.textContent = "";
    queueMicrotask(() => {
      if (isCurrentCartMutation(context) && status.isConnected) {
        status.textContent = message;
      }
    });
  }

  /**
   * @param {CartMutationContext} context - Mutation context.
   * @param {number} quantity - Server-confirmed resulting quantity.
   */
  #announceQuantity(context, quantity) {
    const subtotal = this.#getCartSubtotal();
    const message = subtotal
      ? this.#formatStatus("statusQuantityWithSubtotal", { item: context.itemName, quantity, subtotal })
      : this.#formatStatus("statusQuantity", { item: context.itemName, quantity });

    this.#announceMessage(context, message);
  }

  /** @param {CartMutationContext} context - Mutation context. */
  #announceRemoval(context) {
    const message = this.#formatStatus("statusRemoved", { item: context.itemName });
    this.#announceMessage(context, message);
  }

  /**
   * @param {CartMutationContext} context - Mutation context.
   * @param {string} error - Server-provided cart error.
   */
  #announceError(context, error) {
    const message = this.#formatStatus("statusError", { item: context.itemName, error });
    this.#announceMessage(context, message);
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Releases pending state on every cart surface after the current mutation.
   * @param {number} intentId - Synchronization authority id.
   */
  #releaseCartSyncIntent(intentId) {
    if (!isCurrentCartSyncIntent(intentId)) {
      return;
    }

    for (const item of document.querySelectorAll(CART_ITEMS_COMPONENT)) {
      if (item instanceof HTMLElement) {
        resetShimmer(item);
        item.classList.remove("cart-items-disabled");
      }
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) {
      return;
    }

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = /** @type {HTMLInputElement | null} */ (selector.querySelector("input[data-cart-quantity]"));

        if (input) {
          input.dataset.cartQuantity = item.quantity.toString();

          // Update the quantity selector's internal state
          if ("updateCartQuantity" in selector && typeof selector.updateCartQuantity === "function") {
            selector.updateCartQuantity();
          }
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll("cart-quantity-selector-component")) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) {
      throw new Error("Section id missing");
    }

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get(CART_ITEMS_COMPONENT)) {
  customElements.define(CART_ITEMS_COMPONENT, CartItemsComponent);
}
